import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import VSpace from '../../common/VSpace'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import ChatSessionModal from '../Chats/ChatSessionModal'
import TranscriptDetailModal from './TranscriptDetailModal'
import {
  AdminMediaCapture,
  formatCostUsd,
  formatTokens,
  getDefaultTranscriptFilters,
  isVideoCapture,
  MediaCapturePurpose,
  purposeLabels,
  TranscriptFilters,
  transcriptionStatusColors,
  transcriptionStatusLabels,
  transcriptionStatusOptions,
  TranscriptionStatus,
} from './transcriptsModel'
import { useFetchAudioLinkMutation, useTranscriptsSearchQuery } from './transcriptsQueries'

type FormValues = {
  tenantId?: number
  purpose?: MediaCapturePurpose
  transcriptionStatus?: TranscriptionStatus
  createdRange?: [Dayjs | null, Dayjs | null] | null
  searchText?: string
}

const formValuesToFilters = (values: FormValues, pageSize: number): TranscriptFilters => ({
  tenantId: values.tenantId,
  purpose: values.purpose,
  transcriptionStatus: values.transcriptionStatus,
  createdFromUtc: values.createdRange?.[0]?.startOf('day').toISOString(),
  createdToUtc: values.createdRange?.[1]?.endOf('day').toISOString(),
  searchText: values.searchText,
  page: 1,
  pageSize,
})

const TranscriptsSection = () => {
  const [form] = Form.useForm<FormValues>()
  const [filters, setFilters] = useState<TranscriptFilters>(() => getDefaultTranscriptFilters())
  const [selectedCapture, setSelectedCapture] = useState<AdminMediaCapture | null>(null)
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const tenantsQuery = useSidebarTenantsQuery()
  const { data, isLoading } = useTranscriptsSearchQuery(filters)
  const fetchAudioLink = useFetchAudioLinkMutation()
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [loadingMediaId, setLoadingMediaId] = useState<string | null>(null)
  const [videoPlayback, setVideoPlayback] = useState<{ capture: AdminMediaCapture; link: string } | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // The row that opened the modal is a snapshot from the moment it was clicked. Retrying
  // invalidates the search, so the capture's status moves underneath an open modal — which is
  // exactly when the in-progress guard on the retry button has to fire. Prefer the live row,
  // and keep the snapshot only so the modal survives the capture leaving the current result set.
  const liveSelectedCapture = selectedCapture
    ? data?.items.find((item) => item.id === selectedCapture.id) ?? selectedCapture
    : null

  const stopPlayback = () => {
    audioRef.current?.pause()
    audioRef.current = null
    setPlayingId(null)
  }

  useEffect(() => stopPlayback, [])

  const handlePlayClick = (record: AdminMediaCapture) => {
    if (playingId === record.id) {
      stopPlayback()
      return
    }
    stopPlayback()
    setLoadingMediaId(record.id)
    fetchAudioLink.mutate(
      { id: record.id, tenantId: record.tenantId },
      {
        onSuccess: (link) => {
          if (isVideoCapture(record.mimeType)) {
            setVideoPlayback({ capture: record, link })
            return
          }
          const audio = new Audio(link)
          audio.onended = () => setPlayingId(null)
          audioRef.current = audio
          setPlayingId(record.id)
          void audio.play()
        },
        onSettled: () => setLoadingMediaId(null),
      }
    )
  }

  const handleApply = () => {
    setFilters(formValuesToFilters(form.getFieldsValue(), filters.pageSize))
  }

  const handleReset = () => {
    form.resetFields()
    setFilters(getDefaultTranscriptFilters())
  }

  const columns = [
    {
      title: '',
      key: 'play',
      width: 50,
      render: (_: unknown, record: AdminMediaCapture) => (
        <Button
          type="text"
          size="small"
          loading={loadingMediaId === record.id}
          icon={playingId === record.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            handlePlayClick(record)
          }}
          aria-label={
            isVideoCapture(record.mimeType) ? 'Play video' : playingId === record.id ? 'Pause audio' : 'Play audio'
          }
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAtUtc',
      width: 170,
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Tenant',
      dataIndex: 'tenantId',
      width: 90,
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      width: 130,
      render: (val: MediaCapturePurpose) => purposeLabels[val] ?? val,
    },
    {
      title: 'Status',
      dataIndex: 'transcriptionStatus',
      width: 110,
      render: (val: TranscriptionStatus) => (
        <Tag color={transcriptionStatusColors[val]}>{transcriptionStatusLabels[val] ?? val}</Tag>
      ),
    },
    {
      title: 'By',
      dataIndex: 'createdByEmail',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Tokens',
      key: 'tokens',
      width: 120,
      render: (_: unknown, record: AdminMediaCapture) => formatTokens(record.inputTokens, record.outputTokens),
    },
    {
      title: 'Cost',
      dataIndex: 'transcriptionCostUsd',
      width: 100,
      sorter: (a: AdminMediaCapture, b: AdminMediaCapture) =>
        (a.transcriptionCostUsd ?? 0) - (b.transcriptionCostUsd ?? 0),
      render: (val: number | null) => formatCostUsd(val),
    },
    {
      title: 'Chat',
      dataIndex: 'sessionId',
      width: 90,
      render: (val: string | null) =>
        val ? (
          <Typography.Link
            onClick={(e) => {
              e.stopPropagation()
              setSelectedSessionId(val)
            }}
          >
            Open
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Transcript',
      dataIndex: 'transcript',
      ellipsis: true,
      render: (val: string | null) =>
        val ? (
          <Typography.Text ellipsis style={{ maxWidth: 400 }}>{val}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ]

  return (
    <VSpace>
      <Form form={form} onFinish={handleApply}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Form.Item name="searchText" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
            <Input placeholder="Search transcript text" allowClear />
          </Form.Item>
          <Form.Item name="tenantId" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              allowClear
              placeholder="Tenant"
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item name="purpose" style={{ marginBottom: 0 }}>
            <Select
              allowClear
              placeholder="Purpose"
              style={{ width: 150 }}
              options={[
                { value: 'voice-chat', label: 'Voice chat' },
                { value: 'walkthrough-estimate', label: 'Walkthrough' },
              ]}
            />
          </Form.Item>
          <Form.Item name="transcriptionStatus" style={{ marginBottom: 0 }}>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 150 }}
              options={transcriptionStatusOptions()}
            />
          </Form.Item>
          <Form.Item name="createdRange" style={{ marginBottom: 0 }}>
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">Apply</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button onClick={handleReset}>Reset</Button>
          </Form.Item>
        </div>
      </Form>
      <Table<AdminMediaCapture>
        dataSource={data?.items}
        loading={isLoading}
        rowKey="id"
        columns={columns}
        onRow={(record) => ({
          onClick: () => setSelectedCapture(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: filters.page,
          pageSize: filters.pageSize,
          total: data?.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          showTotal: (total) => `${total} captures`,
          onChange: (page, pageSize) =>
            setFilters((prev) => ({ ...prev, page, pageSize })),
        }}
      />
      <TranscriptDetailModal
        capture={liveSelectedCapture}
        onClose={() => setSelectedCapture(null)}
        onOpenChat={(sessionId) => setSelectedSessionId(sessionId)}
      />
      <ChatSessionModal
        sessionId={selectedSessionId}
        onClose={() => setSelectedSessionId(null)}
      />
      {videoPlayback && (
        <Modal
          open
          onCancel={() => setVideoPlayback(null)}
          footer={null}
          width={720}
          title={videoPlayback.capture.filename}
        >
          <video controls autoPlay src={videoPlayback.link} style={{ width: '100%', maxHeight: '65vh' }} />
        </Modal>
      )}
    </VSpace>
  )
}

export default TranscriptsSection
